import { useLayoutEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";

/**
 * SPA "redirect-only" route helper.
 *
 * React Router's <Navigate replace> is a client-side redirect (not an HTTP 301),
 * so search engines can still index the source URL if they render JS before
 * following the redirect. To keep SEO signals consistent we:
 *   1. Inject <meta name="robots" content="noindex,follow"> so JS-executing
 *      crawlers drop the source URL from the index.
 *   2. Inject/replace <link rel="canonical"> pointing at the destination so
 *      link equity consolidates on the real page.
 *   3. Then perform the client-side Navigate.
 *
 * Tags are removed on unmount so they never leak into the destination route.
 */
export function RedirectRoute({
  to,
  preserveQuery = true,
}: {
  to: string;
  /** Merge the source URL's query params into the destination (destination wins on conflict). */
  preserveQuery?: boolean;
}) {
  const location = useLocation();

  // Forward incoming search params so deep links like /my-learning?enroll=<slug>
  // don't silently lose their intent when the source route is a redirect.
  const destinationPath = (() => {
    if (!preserveQuery || !location.search || to.startsWith("http")) return to;
    const [path, ownQuery = ""] = to.split("?");
    const merged = new URLSearchParams(location.search);
    new URLSearchParams(ownQuery).forEach((value, key) => merged.set(key, value));
    const query = merged.toString();
    return query ? `${path}?${query}` : path;
  })();


  useLayoutEffect(() => {
    const head = document.head;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const destination = to.startsWith("http") ? to : `${origin}${to}`;

    // 1) noindex,follow for the redirect-only source
    const robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex,follow");
    robots.setAttribute("data-redirect-route", "true");
    head.appendChild(robots);

    // 2) canonical -> destination (replace any existing canonical for this render)
    const existingCanonical = head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    const previousCanonicalHref = existingCanonical?.getAttribute("href") ?? null;
    let canonical = existingCanonical;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      canonical.setAttribute("data-redirect-route", "true");
      head.appendChild(canonical);
    }
    canonical.setAttribute("href", destination);

    return () => {
      robots.remove();
      if (canonical?.getAttribute("data-redirect-route") === "true") {
        canonical.remove();
      } else if (canonical && previousCanonicalHref !== null) {
        canonical.setAttribute("href", previousCanonicalHref);
      }
    };
    // Re-run if the source path changes (same component reused across redirects)
  }, [to, location.pathname]);

  return <Navigate to={destinationPath} replace />;
}
